<script setup>
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  sharingEnabled: { type: Boolean, default: false },
  shareUrl: { type: String, default: '' },
  copyingLink: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  templates: { type: Array, default: () => [] },
  selectedTemplateId: { type: [String, null], default: null },
  eventId: { type: String, default: '' },
});

const emit = defineEmits([
  'toggleSharing',
  'copyShareLink',
  'regenerateLink',
  'selectTemplate',
]);

const { t } = useI18n();
const store = useStore();

onMounted(() => {
  const records = store.getters['planningEventPortals/getRecords'];
  if (Array.isArray(records) && records.length) return;
  store.dispatch('planningEventPortals/get');
});

const previewHtml = ref('');
const previewLoading = ref(false);

const fetchPreview = async () => {
  if (!props.selectedTemplateId || !props.eventId) {
    previewHtml.value = '';
    return;
  }
  previewLoading.value = true;
  try {
    const data = await store.dispatch('planningEventPortals/preview', {
      templateId: props.selectedTemplateId,
      eventId: props.eventId,
      shell: true,
    });
    previewHtml.value = data?.html ?? '';
  } catch {
    previewHtml.value = '';
  } finally {
    previewLoading.value = false;
  }
};

watch(() => props.selectedTemplateId, fetchPreview, { immediate: true });
</script>

<template>
  <div class="flex gap-6 min-h-[400px]">
    <!-- Left: sharing controls -->
    <div class="w-72 shrink-0 space-y-4">
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-11">
          {{ t('PLANNING.SHOW.PUBLIC_SHARING') }}
        </span>
        <Switch
          :model-value="sharingEnabled"
          @update:model-value="emit('toggleSharing', $event)"
        />
      </div>

      <template v-if="sharingEnabled">
        <div class="flex gap-2">
          <input
            :value="shareUrl"
            type="text"
            readonly
            class="reset-base flex-1 px-3 py-2 text-sm bg-n-slate-3 border border-n-slate-6 rounded-lg text-n-slate-12"
          />
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors"
            @click="emit('copyShareLink')"
          >
            <Icon
              :icon="copyingLink ? 'i-lucide-check' : 'i-lucide-copy'"
              class="size-4"
            />
          </button>
        </div>

        <button
          class="w-full px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-slate-3 rounded-lg hover:bg-n-slate-4 transition-colors"
          @click="emit('regenerateLink')"
        >
          {{ t('PLANNING.SHOW.REGENERATE_LINK') }}
        </button>

        <!-- Event Page Template selector -->
        <div class="border-t border-n-slate-6 pt-4">
          <label class="text-sm font-semibold text-n-slate-12 mb-2 block">
            {{ t('PLANNING.SHOW.EVENT_PORTAL') }}
          </label>
          <select
            :value="selectedTemplateId || ''"
            class="w-full px-3 py-2 text-sm border border-n-slate-6 rounded-lg bg-n-slate-3 text-n-slate-12"
            @change="emit('selectTemplate', $event.target.value || null)"
          >
            <option value="">
              {{ t('PLANNING.SHOW.NO_TEMPLATE') }}
            </option>
            <option v-for="tpl in templates" :key="tpl.id" :value="tpl.id">
              {{ tpl.name }}
            </option>
          </select>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ t('PLANNING.SHOW.SHARE_WARNING') }}
        </p>
      </template>
    </div>

    <!-- Right: template preview -->
    <div
      class="flex-1 min-w-0 rounded-lg border border-n-slate-6 bg-n-slate-3 overflow-hidden"
    >
      <template v-if="sharingEnabled && selectedTemplateId">
        <div
          v-if="previewLoading"
          class="flex items-center justify-center h-full"
        >
          <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-9" />
        </div>
        <iframe
          v-else-if="previewHtml"
          :srcdoc="previewHtml"
          class="w-full h-full border-0"
          sandbox="allow-scripts"
        />
      </template>
      <div
        v-else
        class="flex flex-col items-center justify-center h-full gap-2 text-n-slate-9"
      >
        <span class="i-lucide-eye size-8" />
        <p class="text-sm">
          {{
            !sharingEnabled
              ? t('PLANNING.SHOW.PREVIEW_ENABLE_SHARING')
              : t('PLANNING.SHOW.PREVIEW_SELECT_TEMPLATE')
          }}
        </p>
      </div>
    </div>
  </div>
</template>
